/**
 * BANK-IBAN-R3.9 — Customer UI bootstrap cache (server is SoT).
 */
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import {
  readCheckoutPrefill,
  telegramUserId,
  writeCheckoutPrefill,
  type CustomerProfileV2,
} from "./customer-profile-v2.js";
import { waitForTelegramInitData } from "../lib/telegram.js";
import { fetchWithTimeout, isFetchTimeout } from "./fetch-timeout-v2.js";
import { setRuntimeCustomerPayment } from "./customer-payment-flags-v2.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";

export type CustomerBootstrapPayload = {
  ok: true;
  environment: string;
  customer_build_id: string;
  app_rev: string;
  customer_id: string | null;
  telegram_user_id: string | null;
  identity_source?: string;
  consent: {
    required: boolean;
    current_version: string;
    accepted_version: string | null;
  };
  marketing_permission?: {
    state: "UNKNOWN" | "GRANTED" | "WITHDRAWN" | "DENIED";
    source: string | null;
    copy_version: string | null;
    policy_version: string | null;
  };
  profile: {
    first_name: string;
    last_name: string;
    phone: string;
    completed: boolean;
  };
  delivery: {
    remembered: boolean;
    method: string | null;
    city: string | null;
    warehouse: string | null;
    postomat: string | null;
    address: string | null;
    provider?: string | null;
    city_ref?: string | null;
    warehouse_ref?: string | null;
    locker_ref?: string | null;
    courier_address?: string | null;
    recipient_name?: string | null;
    recipient_phone?: string | null;
    np_selection?: Record<string, unknown> | null;
    validated_at?: string | null;
    last_used_at?: string | null;
    source_order_id?: string | null;
  };
  payment: {
    mode: string;
    allowed_methods: Array<"novapay_card" | "bank_transfer_fop" | "nova_poshta_cod">;
    rail: string | null;
  };
  customer_runtime: {
    expected_app_rev: string;
    canonical_origin: string;
    payment_mode: string;
  };
};

let cached: CustomerBootstrapPayload | null = null;

export function getCachedCustomerBootstrap(): CustomerBootstrapPayload | null {
  return cached;
}

export function setCachedCustomerBootstrap(next: CustomerBootstrapPayload | null): void {
  cached = next;
}

function ownerTgFromUrl(): string | null {
  try {
    const q = new URLSearchParams(window.location.search).get("owner_tg");
    if (q && /^\d+$/.test(q)) return q;
  } catch {
    /* ignore */
  }
  return null;
}

function npSelectionFromBootstrap(
  delivery: CustomerBootstrapPayload["delivery"],
  existing: CustomerProfileV2 | null,
): NovaPoshtaReferenceSelection | undefined {
  const raw = delivery.np_selection;
  if (raw && typeof raw === "object" && typeof (raw as { settlementRef?: unknown }).settlementRef === "string") {
    return raw as unknown as NovaPoshtaReferenceSelection;
  }
  return existing?.np_selection;
}

export function applyBootstrapToLocalCache(payload: CustomerBootstrapPayload): void {
  cached = payload;
  setRuntimeCustomerPayment(payload.payment);
  const existing = readCheckoutPrefill();
  const np = npSelectionFromBootstrap(payload.delivery, existing);
  const city =
    payload.delivery.city ||
    (typeof np?.settlementLabel === "string" ? np.settlementLabel : "") ||
    existing?.city ||
    "";
  const warehouse =
    payload.delivery.warehouse ||
    payload.delivery.postomat ||
    payload.delivery.courier_address ||
    (typeof np?.warehouseLabel === "string" ? np.warehouseLabel : "") ||
    existing?.warehouse ||
    "";
  const keepName =
    existing?.name_source === "user" || existing?.name_source === "profile" ? existing : null;
  writeCheckoutPrefill({
    name: (keepName?.name || payload.profile.first_name || existing?.name || "").slice(0, 80),
    surname: (keepName?.surname || payload.profile.last_name || existing?.surname || "").slice(0, 80),
    phone: payload.profile.phone || existing?.phone || "",
    city,
    warehouse,
    note: existing?.note ?? "",
    // name_source: "telegram"
    name_source: keepName?.name_source ?? "telegram",
    ...(np ? { np_selection: np } : {}),
    ...(existing?.email ? { email: existing.email } : {}),
    ...(existing?.birthday ? { birthday: existing.birthday } : {}),
  });
}

export async function fetchCustomerBootstrap(sessionKey: string): Promise<CustomerBootstrapPayload | null> {
  try {
    await waitForTelegramInitData(1800);
    const qs = new URLSearchParams();
    if (sessionKey) qs.set("session_key", sessionKey);
    const uid = telegramUserId() ?? ownerTgFromUrl();
    if (uid) {
      qs.set("telegram_user_id", uid);
      qs.set("owner_tg", uid);
    }
    // AP3R3R1G1 — bound bootstrap network; never leave shell waiting on hung Worker.
    const res = await fetchWithTimeout(
      `/api/store-v2/customer-bootstrap?${qs.toString()}`,
      {
        method: "GET",
        headers: storeV2AuthHeaders({ Accept: "application/json" }),
      },
      4000,
    );
    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) return null;
    const json = (await res.json()) as CustomerBootstrapPayload & { ok?: boolean };
    if (!res.ok || json.ok !== true) return null;
    applyBootstrapToLocalCache(json);
    return json;
  } catch (err) {
    if (isFetchTimeout(err)) return null;
    return null;
  }
}

export async function persistCustomerRuntimeProfile(input: {
  first_name: string;
  last_name: string;
  phone: string;
  city?: string;
  warehouse?: string;
  np_selection?: NovaPoshtaReferenceSelection | null;
  source_order_id?: string | null;
  durable?: boolean;
}): Promise<CustomerBootstrapPayload | null> {
  try {
    await waitForTelegramInitData(800);
    const uid = telegramUserId() ?? ownerTgFromUrl();
    const durable = input.durable !== false;
    const source =
      typeof input.source_order_id === "string" && /^STG-/i.test(input.source_order_id)
        ? input.source_order_id
        : null;
    const payload: Record<string, unknown> = {
      telegram_user_id: uid,
      owner_tg: uid,
      first_name: input.first_name,
      last_name: input.last_name,
      phone: input.phone,
    };
    if (durable) {
      const sel = input.np_selection ?? null;
      const mode = sel?.mode;
      const isLocker = mode === "parcel_locker";
      const isCourier = mode === "address";
      const city = input.city ?? sel?.settlementLabel ?? null;
      const warehouseLabel = input.warehouse ?? sel?.warehouseLabel ?? null;
      payload.delivery = {
        remembered: Boolean(city || warehouseLabel || sel?.settlementRef),
        provider: "nova_poshta",
        method: isLocker
          ? "NOVA_POSHTA_PARCEL_LOCKER"
          : isCourier
            ? "NOVA_POSHTA_COURIER"
            : "NOVA_POSHTA_BRANCH",
        city,
        warehouse: isLocker || isCourier ? null : warehouseLabel,
        postomat: isLocker ? warehouseLabel : null,
        address: isCourier ? sel?.addressLine ?? warehouseLabel : null,
        city_ref: sel?.cityRef ?? sel?.settlementRef ?? null,
        warehouse_ref: isLocker || isCourier ? null : sel?.warehouseRef ?? null,
        locker_ref: isLocker ? sel?.warehouseRef ?? null : null,
        courier_address: isCourier ? sel?.addressLine ?? null : null,
        np_selection: sel,
        last_used_at: new Date().toISOString(),
        source_order_id: source,
      };
    }
    const res = await fetch("/api/store-v2/customer-bootstrap", {
      method: "POST",
      headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
    const json = (await res.json()) as CustomerBootstrapPayload & { ok?: boolean };
    if (!res.ok || json.ok !== true) return null;
    applyBootstrapToLocalCache(json);
    return json;
  } catch {
    return null;
  }
}

export function normalizeBootstrapForParity(
  payload: CustomerBootstrapPayload,
): Record<string, unknown> {
  return {
    customer_id: payload.customer_id,
    telegram_user_id: payload.telegram_user_id,
    consent: payload.consent,
    profile: payload.profile,
    delivery: payload.delivery,
    payment: payload.payment,
  };
}
